import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { COST_BASIS_ROUTE, t, type StringKey } from '@bms2/shared';

/**
 * 🔒 A refusal that names a page to fix it on carries that page's route beside its sentence (T1191), and the screen draws the
 * route as a link after the sentence. A refusal with no route reads as it always did.
 *
 * 🔒 A refusal whose body says the route is not open to the viewer (`routeOpen: false`, T3553) draws the sentence alone: the
 * web holds no signal of a viewer's module access, so the server says, and a body that says nothing draws the link.
 */
export function refusalRoute(body: unknown): string | null {
  const route = body !== null && typeof body === 'object' ? (body as { route?: unknown }).route : null;
  if (body !== null && typeof body === 'object' && (body as { routeOpen?: unknown }).routeOpen === false) return null;
  return typeof route === 'string' && route.startsWith('/') && !route.startsWith('//') ? route : null;
}

/** 🔨 The link's words are keyed to the page the route opens (T1361); a route with no entry here reads as the generic Open the Page. */
const ROUTE_LABELS: Readonly<Record<string, StringKey>> = { [COST_BASIS_ROUTE]: 'refusal.openAccountingBasis' };

export function RefusalText({ message, body }: { message: string; body?: unknown }): ReactNode {
  const route = refusalRoute(body);
  if (route === null) return message;
  return (
    <>
      {message} <Link to={route} data-refusal-link={route}>{t(ROUTE_LABELS[route] ?? 'refusal.openPage')}</Link>
    </>
  );
}
